// Solana-Wallet-Import via Helius Enhanced Transactions API.
//
// User gibt seine Solana-Adresse (z.B. Phantom-Pubkey) + optionales Label ein,
// die App ruft /api/solana-wallet-sync auf und übergibt die geparsten TX an den
// Parent via onImport — identischer Flow wie CSV-Import.
//
// Privacy: die Adresse wird nur kurz an unseren Vercel-Endpoint geschickt,
// nicht persistiert. Helius bekommt die Adresse zwar zwecks Lookup, aber nur
// über unser Backend — kein direkter Drittanbieter-Connect im Browser.

import { useState, useEffect } from 'react';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import InfoBanner from './InfoBanner';
import { authHeader } from '../services/authHeader';
import { useAuth } from '../context/AuthContext';
import { ensureOwnWallets } from '../services/walletRegistry';
import { findMoonPayLedgerPairs, buildWalletEnrichment } from '../utils/moonpayLedgerDedupe';

const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const API_BASE = process.env.REACT_APP_API_BASE || '';

// Minimales Mapping: nur Coins, deren historische Preise CoinGecko-Demo zuverlässig liefert.
const BACKFILL_COIN_IDS = new Set([
  'solana', 'usd-coin', 'tether', 'bonk', 'jito-governance-token',
  'dogwifcoin', 'jupiter-exchange-solana', 'pyth-network', 'msol',
  'lido-staked-sol', 'render-token', 'orca', 'ethereum', 'bitcoin',
]);

// Phantom-Provider-Lookup: Phantom injiziert window.phantom.solana (neu) und
// window.solana (legacy). Beide tragen .isPhantom.
function getPhantomProvider() {
  if (typeof window === 'undefined') return null;
  const p = window.phantom?.solana;
  if (p?.isPhantom) return p;
  if (window.solana?.isPhantom) return window.solana;
  return null;
}

// Mobile-Browser ohne Phantom-Injection → Phantom-App via Deeplink öffnen,
// dort lädt unsere Site im In-App-Browser MIT window.solana.
function openPhantomDeeplink() {
  if (typeof window === 'undefined') return;
  const url = encodeURIComponent(window.location.href);
  const ref = encodeURIComponent(window.location.origin);
  window.location.href = `https://phantom.app/ul/browse/${url}?ref=${ref}`;
}

export default function SolanaWalletConnect({ onBack, onImport, txs = [], patchTx, deleteTx, showToast }) {
  const { T } = useTheme();
  const { t } = useT();
  const { user } = useAuth();
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('Phantom');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectedKey, setConnectedKey] = useState(null);
  const [providerAvailable, setProviderAvailable] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null); // {done, total}
  const [pairing, setPairing] = useState(false);

  // Rueckwirkende MoonPay-Wallet-Paare im bestehenden Bestand finden
  const moonpayWalletPairs = findMoonPayLedgerPairs(txs || []);

  // TX die einen Backfill brauchen: preis===0/null, coinId bekannt, hat docId+datum.
  const backfillCandidates = (txs || []).filter(tx => {
    if (!tx._docId || !tx.datum) return false;
    if (tx.typ === 'swap') {
      const needsOut = (!tx.preis || tx.preis === 0) && tx.coinIdOut && BACKFILL_COIN_IDS.has(tx.coinIdOut) && tx.mengeOut > 0;
      const needsIn  = (!tx.preis || tx.preis === 0) && tx.coinIdIn  && BACKFILL_COIN_IDS.has(tx.coinIdIn)  && tx.mengeIn  > 0;
      return needsOut || needsIn;
    }
    if (tx.preis && tx.preis !== 0) return false;
    if (!tx.coinId || !BACKFILL_COIN_IDS.has(tx.coinId)) return false;
    return tx.menge > 0;
  });

  const handleBackfillPrices = async () => {
    if (backfillCandidates.length === 0 || backfilling || !patchTx) return;
    setBackfilling(true);
    setError(null);
    setInfo(null);

    // Eindeutige (coinId, datum)-Paare sammeln
    const keys = new Set();
    const jobs = [];
    for (const tx of backfillCandidates) {
      const collectKey = (coinId, datum) => {
        const k = `${coinId}|${datum}`;
        if (!keys.has(k)) { keys.add(k); jobs.push({ coinId, datum }); }
      };
      if (tx.typ === 'swap') {
        if (tx.coinIdOut && BACKFILL_COIN_IDS.has(tx.coinIdOut)) collectKey(tx.coinIdOut, tx.datum);
        if (tx.coinIdIn  && BACKFILL_COIN_IDS.has(tx.coinIdIn))  collectKey(tx.coinIdIn,  tx.datum);
      } else {
        collectKey(tx.coinId, tx.datum);
      }
    }

    setBackfillProgress({ done: 0, total: jobs.length });
    const auth = await authHeader(); // Firebase-ID-Token mitgeben (sonst 401)
    const priceMap = {};
    // Fehlergründe zählen, damit "0 nachgerüstet" nicht stumm bleibt, sondern
    // den echten Grund nennt (häufigster Fall: CoinGecko-Demo liefert keine
    // Preise älter als 365 Tage → 404 mit "Demo-Tier"-Hinweis).
    const reasons = { tooOld: 0, noKey: 0, rateLimit: 0, notFound: 0, other: 0 };
    for (let i = 0; i < jobs.length; i++) {
      const { coinId, datum } = jobs[i];
      try {
        const r = await fetch(
          `${API_BASE}/api/coin-price-history?coin=${encodeURIComponent(coinId)}&date=${encodeURIComponent(datum)}&currency=eur`,
          { headers: auth },
        );
        if (r.ok) {
          const data = await r.json();
          if (typeof data.price === 'number') priceMap[`${coinId}|${datum}`] = data.price;
          // Daten-Lücken kommen jetzt als 200 mit price:null + reason (statt 404).
          else if (data?.reason === 'demo-range') reasons.tooOld++;
          else reasons.notFound++;
        } else {
          const data = await r.json().catch(() => ({}));
          const m = String(data?.error || '').toLowerCase();
          if (r.status === 503) reasons.noKey++;
          else if (r.status === 404 && m.includes('demo-tier')) reasons.tooOld++;
          else if (r.status === 429) reasons.rateLimit++;
          else if (r.status === 404) reasons.notFound++;
          else reasons.other++;
        }
      } catch { reasons.other++; }
      setBackfillProgress({ done: i + 1, total: jobs.length });
      // sanftes Throttling, falls Edge-Cache kalt ist
      if (i < jobs.length - 1) await new Promise(r => setTimeout(r, 80));
    }

    // Patches anwenden
    let patched = 0;
    for (const tx of backfillCandidates) {
      try {
        if (tx.typ === 'swap') {
          const pOut = tx.coinIdOut ? priceMap[`${tx.coinIdOut}|${tx.datum}`] : null;
          const pIn  = tx.coinIdIn  ? priceMap[`${tx.coinIdIn}|${tx.datum}`]  : null;
          let preis = null;
          if (pOut != null && tx.mengeOut > 0)      preis = pOut * tx.mengeOut;
          else if (pIn != null && tx.mengeIn > 0)   preis = pIn  * tx.mengeIn;
          if (preis != null && Number.isFinite(preis)) {
            await patchTx(tx._docId, { preis });
            patched++;
          }
        } else {
          const p = priceMap[`${tx.coinId}|${tx.datum}`];
          if (p != null && tx.menge > 0) {
            const preis = p * tx.menge;
            const stueck = tx.menge > 0 ? preis / tx.menge : 0;
            await patchTx(tx._docId, { preis, stueck });
            patched++;
          }
        }
      } catch (err) {
        console.warn('[backfill] patch fehlgeschlagen', tx._docId, err.message);
      }
    }

    setBackfilling(false);
    setBackfillProgress(null);
    const hints = [];
    if (reasons.tooOld)    hints.push(t('con_backfill_hint_too_old', { n: reasons.tooOld }));
    if (reasons.noKey)     hints.push(t('con_backfill_hint_no_key'));
    if (reasons.rateLimit) hints.push(t('con_backfill_hint_rate_limit'));
    if (reasons.notFound)  hints.push(t('con_backfill_hint_not_found', { n: reasons.notFound }));
    const msg = t('con_backfill_done', { patched, total: backfillCandidates.length })
      + (hints.length ? ' ' + t('con_backfill_hint_prefix', { hints: hints.join('; ') }) : '');
    setInfo(msg);
    showToast?.(patched > 0 ? t('con_backfill_toast_ok', { n: patched }) : t('con_backfill_toast_none'));
  };

  useEffect(() => {
    setProviderAvailable(!!getPhantomProvider());
  }, []);

  const isMobile = typeof navigator !== 'undefined'
    && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  const handleConnectPhantom = async () => {
    setError(null);
    setInfo(null);
    const provider = getPhantomProvider();
    if (!provider) {
      if (isMobile) {
        openPhantomDeeplink();
        return;
      }
      setError(t('con_phantom_not_found'));
      return;
    }
    setConnecting(true);
    try {
      const resp = await provider.connect();
      const pubkey = resp?.publicKey?.toString?.() || provider.publicKey?.toString?.() || '';
      if (!SOLANA_ADDRESS_RE.test(pubkey)) {
        throw new Error(t('con_phantom_no_address'));
      }
      setAddress(pubkey);
      setConnectedKey(pubkey);
      setLabel(l => l || 'Phantom');
    } catch (err) {
      if (err?.code === 4001) {
        setError(t('con_phantom_rejected'));
      } else {
        setError(err?.message || t('con_phantom_failed'));
      }
    } finally {
      setConnecting(false);
    }
  };

  const handlePairExisting = async () => {
    if (!moonpayWalletPairs.length || pairing || !patchTx || !deleteTx) return;
    setPairing(true);
    setError(null);
    setInfo(null);
    let merged = 0;
    for (const { ledger: walletTx, moonpay } of moonpayWalletPairs) {
      try {
        if (moonpay?._docId) {
          const enrichment = buildWalletEnrichment(walletTx);
          await patchTx(moonpay._docId, enrichment);
        }
        if (walletTx?._docId) {
          await deleteTx(walletTx);
        }
        merged++;
      } catch (err) {
        console.warn('[pair] fehlgeschlagen', err.message);
      }
    }
    setPairing(false);
    const msg = t('con_moonpay_cleaned', { n: merged });
    setInfo(msg);
    showToast?.(`✓ ${msg}`);
  };

  const handleDisconnectPhantom = async () => {
    const provider = getPhantomProvider();
    try { await provider?.disconnect?.(); } catch { /* ignore */ }
    setConnectedKey(null);
    setAddress('');
  };

  const addressOk = SOLANA_ADDRESS_RE.test(address.trim());
  const shortKey = connectedKey
    ? `${connectedKey.slice(0, 4)}…${connectedKey.slice(-4)}`
    : '';

  const handleImport = async () => {
    setError(null);
    setInfo(null);
    if (!addressOk) {
      setError(t('con_sol_addr_invalid_paste'));
      return;
    }
    setBusy(true);
    try {
      const auth = await authHeader();
      const res = await fetch(`${API_BASE}/api/solana-wallet-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ address: address.trim(), label: label.trim() || 'Phantom' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || t('con_error_status', { status: res.status }));
      }
      const txs = data.transactions || [];
      if (txs.length === 0) {
        setInfo(t('con_sol_no_txs', { addr: data.address.slice(0, 8) }));
        setBusy(false);
        return;
      }
      setInfo(t('con_sol_parsed', { parsed: data.parsed, fetched: data.fetched }));
      // Die gesyncte Adresse IST Florians eigene Wallet → selbst in „Meine Wallets"
      // registrieren (vor onImport, damit der Eigen-Transfer-Abgleich in
      // handleImportCsv sie sofort kennt). Idempotent, fehlertolerant.
      try {
        const synced = data.address || address.trim();
        if (user?.uid && synced) {
          await ensureOwnWallets(user.uid, [{ label: label.trim() || 'Phantom', chain: 'solana', address: synced }]);
        }
      } catch { /* Registrierung darf den Import nie blockieren */ }
      // Übergabe an den gleichen Flow wie CSV: Review-Screen.
      // WICHTIG: Solana-Rows tragen den Chain-Hash als `hash`; handleImportCsv
      // erkennt On-Chain-Importe aber an `txHash` (wie alle anderen Wallet-Parser
      // — Blockchair/Horizon/rippled). Ohne txHash liefen die Transfers fälschlich
      // als CSV durch enrichTransactions („Blockchain wird geprüft…") = erneute
      // On-Chain-Verifikation → Import HÄNGT, nichts wird gespeichert. txHash
      // spiegeln → blockchainVerified=true → Schritt wird übersprungen.
      await onImport(txs.map(tx => (tx && tx.hash && !tx.txHash) ? { ...tx, txHash: tx.hash } : tx));
    } catch (err) {
      setError(err.message || t('con_sol_sync_failed'));
    } finally {
      setBusy(false);
    }
  };

  const inputBase = {
    width: '100%', padding: '13px 15px', borderRadius: R.input,
    border: `1px solid ${T.line || T.border}`, background: T.surface, color: T.text,
    fontFamily: F.family, fontSize: 14, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '16px', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        {/* Native App = Ionic-Route mit Swipe-back → Pfeil entfällt; Web behält ihn. */}
        {!(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) && (
          <button onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 6, color: T.text, WebkitTapHighlightColor: 'transparent',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        )}
        <div style={{
          fontFamily: F.display, fontSize: 22, fontWeight: 700,
          letterSpacing: '-0.03em', color: T.text,
        }}>{t('con_phantom_title')}</div>
      </div>

      <InfoBanner tone="info" title={t('con_how_it_works')}>
        {t('con_phantom_how_body')}
      </InfoBanner>

      {/* ── Phantom-Connect ─────────────────────────────────────────────── */}
      <div style={{ marginTop: 18 }}>
        {!connectedKey ? (
          <button
            onClick={handleConnectPhantom}
            disabled={connecting || busy}
            style={{
              width: '100%', padding: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: '#AB9FF2', // Phantom-Lila
              color: '#FFFFFF', border: 'none', borderRadius: R.md,
              fontFamily: F.family, fontSize: 15, fontWeight: 700,
              cursor: (connecting || busy) ? 'not-allowed' : 'pointer',
              opacity: connecting ? 0.7 : 1,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {/* Phantom-Geist-Glyph */}
            <svg width="20" height="20" viewBox="0 0 128 128" fill="currentColor" aria-hidden="true">
              <path d="M64 8C33.6 8 9 32.6 9 63v44c0 5 4 9 9 9h14c5 0 9-4 9-9V92h12v15c0 5 4 9 9 9h14c5 0 9-4 9-9V92h12v15c0 5 4 9 9 9h14c5 0 9-4 9-9V63c0-30.4-24.6-55-55-55zM44 64a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm40 0a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"/>
            </svg>
            {connecting ? t('con_phantom_opening')
              : providerAvailable ? t('con_phantom_connect')
              : isMobile ? t('con_phantom_open_app')
              : t('con_phantom_install')}
          </button>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderRadius: R.md,
            background: T.surface, border: `1px solid ${T.line || T.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 14, background: '#AB9FF2', color: '#fff',
                flexShrink: 0,
              }}>
                <svg width="16" height="16" viewBox="0 0 128 128" fill="currentColor" aria-hidden="true">
                  <path d="M64 8C33.6 8 9 32.6 9 63v44c0 5 4 9 9 9h14c5 0 9-4 9-9V92h12v15c0 5 4 9 9 9h14c5 0 9-4 9-9V92h12v15c0 5 4 9 9 9h14c5 0 9-4 9-9V63c0-30.4-24.6-55-55-55zM44 64a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm40 0a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"/>
                </svg>
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: F.family }}>
                  {t('con_phantom_connected')}
                </div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: T.sub }}>
                  {shortKey}
                </div>
              </div>
            </div>
            <button
              onClick={handleDisconnectPhantom}
              style={{
                background: 'none', border: 'none', color: T.sub,
                fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 6,
                fontFamily: F.family, WebkitTapHighlightColor: 'transparent',
              }}
            >{t('con_disconnect')}</button>
          </div>
        )}
      </div>

      {/* ── Manuelle Eingabe (Trenner) ───────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        margin: '18px 0 6px',
      }}>
        <div style={{ flex: 1, height: 1, background: T.line || T.border }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: T.sub, letterSpacing: '0.08em' }}>
          {t('con_or_manual')}
        </span>
        <div style={{ flex: 1, height: 1, background: T.line || T.border }} />
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={{
          display: 'block', fontSize: 12, fontWeight: 600, color: T.sub,
          letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6,
        }}>{t('con_sol_address')}</label>
        <input
          value={address}
          onChange={e => setAddress(e.target.value)}
          placeholder="z.B. 91ZsfBCwZfujPCbT7Xt8AZBJqtJdomGZ6fmkf8utEhxB"
          autoCapitalize="off" autoCorrect="off" spellCheck={false}
          style={{
            ...inputBase,
            fontFamily: F.mono, fontSize: 12,
            borderColor: address && !addressOk ? T.red : (T.line || T.border),
          }}
        />
        {address && !addressOk && (
          <div style={{ fontSize: 11, color: T.red, marginTop: 4, fontFamily: F.family }}>
            {t('con_sol_addr_base58')}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <label style={{
          display: 'block', fontSize: 12, fontWeight: 600, color: T.sub,
          letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6,
        }}>{t('con_label_optional')}</label>
        <input
          value={label}
          onChange={e => setLabel(e.target.value.slice(0, 40))}
          placeholder={t('con_label_placeholder')}
          style={inputBase}
        />
        <div style={{ fontSize: 11, color: T.sub, marginTop: 4, fontFamily: F.family }}>
          {t('con_label_hint')}
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 14 }}>
          <InfoBanner tone="error" title={t('con_error')}>{error}</InfoBanner>
        </div>
      )}
      {info && !error && (
        <div style={{ marginTop: 14 }}>
          <InfoBanner tone="info" title={t('con_status')}>{info}</InfoBanner>
        </div>
      )}

      <button
        onClick={handleImport}
        disabled={!addressOk || busy}
        style={{
          marginTop: 22, width: '100%', padding: '14px',
          background: addressOk && !busy ? T.redGradient : T.line,
          color: T.white, border: 'none', borderRadius: R.md,
          fontFamily: F.family, fontSize: 15, fontWeight: 700,
          cursor: addressOk && !busy ? 'pointer' : 'not-allowed',
          opacity: busy ? 0.7 : 1,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {busy ? t('con_sol_loading_txs') : t('con_import')}
      </button>

      <div style={{
        marginTop: 16, fontSize: 11, color: T.sub, lineHeight: 1.5,
        fontFamily: F.family,
      }}>
        {t('con_sol_readonly_note')}
      </div>

      {/* ── Rueckwirkendes MoonPay-Wallet-Pairing ───────────────────────── */}
      {moonpayWalletPairs.length > 0 && (
        <div style={{
          marginTop: 22, padding: '14px 16px',
          background: T.surface, border: `1px solid ${T.line || T.border}`,
          borderRadius: R.md,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: F.family }}>
            {t('con_moonpay_title')}
          </div>
          <div style={{ fontSize: 11, color: T.sub, marginTop: 4, lineHeight: 1.5 }}>
            {t('con_moonpay_body', { n: moonpayWalletPairs.length })}
          </div>
          <button
            onClick={handlePairExisting}
            disabled={pairing}
            style={{
              marginTop: 10, width: '100%', padding: '11px',
              background: pairing ? T.line : T.redGradient,
              color: T.white, border: 'none', borderRadius: R.md,
              fontFamily: F.family, fontSize: 13, fontWeight: 700,
              cursor: pairing ? 'wait' : 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {pairing ? t('con_processing') : t('con_moonpay_cleanup_btn', { n: moonpayWalletPairs.length })}
          </button>
        </div>
      )}

      {/* ── Preis-Backfill für bestehende TX ohne EUR-Wert ──────────────── */}
      {/* Versteckt (Überbleibsel): Backfill läuft jetzt zentral, nicht hier im Verbinden-Flow. */}
      {false && backfillCandidates.length > 0 && (
        <div style={{
          marginTop: 22, padding: '14px 16px',
          background: T.surface, border: `1px solid ${T.line || T.border}`,
          borderRadius: R.md,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: F.family }}>
            {t('con_backfill_title')}
          </div>
          <div style={{ fontSize: 11, color: T.sub, marginTop: 4, lineHeight: 1.5 }}>
            {t('con_backfill_body', { n: backfillCandidates.length })}
          </div>
          <button
            onClick={handleBackfillPrices}
            disabled={backfilling}
            style={{
              marginTop: 10, width: '100%', padding: '11px',
              background: backfilling ? T.line : T.redGradient,
              color: T.white, border: 'none', borderRadius: R.md,
              fontFamily: F.family, fontSize: 13, fontWeight: 700,
              cursor: backfilling ? 'wait' : 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {backfilling
              ? t('con_backfill_loading', { done: backfillProgress?.done || 0, total: backfillProgress?.total || 0 })
              : t('con_backfill_btn', { n: backfillCandidates.length })}
          </button>
        </div>
      )}
    </div>
  );
}
